import mixpanel from "mixpanel-browser";
import { sanitizeMixpanelEvent } from "./sanitize";
import { getSafeUserContext } from "./context";

let initialized = false;

// Rewrites any mp_ / __mp_ cookies that the Mixpanel SDK sets to include
// SameSite=Strict and Secure, since the SDK (v2.x) doesn't expose those
// attributes through its init config. Called after opt_in_tracking /
// opt_out_tracking so the cookies exist before we patch them.
const patchMixpanelCookieSameSite = () => {
  document.cookie.split(";").forEach((rawCookie) => {
    const eqIdx = rawCookie.indexOf("=");
    if (eqIdx === -1) return;
    const name = rawCookie.slice(0, eqIdx).trim();
    if (!name.startsWith("mp_") && !name.startsWith("__mp_")) return;
    const value = rawCookie.slice(eqIdx + 1).trim();
    document.cookie = `${name}=${value}; path=/; SameSite=Strict; Secure`;
  });
};

export const initMixpanel = ({ token, enabled }) => {
  if (!token) {
    console.warn("[Mixpanel] No token provided. Analytics will not work. Check REACT_APP_MIXPANEL_TOKEN environment variable.");
    return false;
  }
  if (!enabled) {
    if (initialized) {
      mixpanel.opt_out_tracking();
      patchMixpanelCookieSameSite();
    }
    return false;
  }
  if (initialized) return true;

  try {
    mixpanel.init(token, {
      api_host: "https://api.mixpanel.com",
      ip: false, // IP anonymization enabled
      ignore_dnt: true,
      persistence: "localStorage",
      // Disable automatic device/browser tracking
      track_pageview: false, // We track page views manually with sanitized data
      secure_cookie: true,           // Adds Secure flag to any SDK-set cookies
      cross_subdomain_cookie: false, // Restricts cookies to app.123wellness.org
    });

    // Disable only device identifiers (not helpful device info)
    try {
      mixpanel.register({
        $device_id: null, // Don't track device ID (identifying)
        $browser_version: null, // Don't track browser version (can be identifying)
        $screen_resolution: null, // Don't track exact resolution (can be identifying)
        $host: null, // Don't track host
      });
    } catch (e) {
      // Mixpanel might not be ready yet, will be set in setMixpanelUser
    }
    mixpanel.opt_in_tracking();
    patchMixpanelCookieSameSite();
    initialized = true;
    console.log("[Mixpanel] Initialized successfully");
    return true;
  } catch (error) {
    console.error("[Mixpanel] Initialization failed:", error);
    return false;
  }
};

export const resetMixpanel = () => {
  if (!initialized) return;
  mixpanel.reset();
  initialized = false;
};

// Tokenize user ID to prevent identification
export const tokenizeUserId = (userId) => {
  if (!userId) return null;
  // Simple hash function for tokenization (one-way, can't reverse)
  let hash = 0;
  const str = String(userId);
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `user_${Math.abs(hash)}`;
};

export const setMixpanelUser = (user) => {
  if (!initialized) return;
  if (!user?._id) return;
  
  // Capture device_id BEFORE identify() for ID merge
  const deviceId = mixpanel.get_distinct_id();
  
  // Use tokenized ID instead of raw user ID
  const tokenizedId = tokenizeUserId(user._id);
  mixpanel.identify(tokenizedId);
  
  // Disable only device identifiers (not helpful device info)
  mixpanel.register({
    $device_id: null, // Don't track device ID (identifying)
    $browser_version: null, // Don't track browser version (can be identifying)
    $screen_resolution: null, // Don't track exact resolution (can be identifying)
    $host: null, // Don't track host
  });
  
  const context = getSafeUserContext(user);
  // Only register safe, aggregated context - no identifying data
  mixpanel.register({
    role: context.role || "unknown",
    is_demo: Boolean(context.is_demo),
    // Only include city/country if they're generic enough (not specific addresses)
    // Consider removing these if they could identify users
    city: context.city || undefined,
    country: context.country || undefined,
    // School ID is tokenized to prevent identification
    school_id: context.school_id ? tokenizeUserId(context.school_id) : undefined,
    // District ID is tokenized to prevent identification
    district_id: context.district_id ? tokenizeUserId(context.district_id) : undefined,
    // Timezone and locale are broad enough to be safe for cohorting
    timezone: context.timezone || undefined,
    locale: context.locale ? context.locale.split("-")[0] : undefined, // Only language, not region
  });

  // Emit a post-identify event with BOTH $device_id and $user_id to trigger merge
  // This must bypass sanitization/scrubbing to ensure $device_id is included
  const identifyProps = {
    page: window.location.pathname,
    $device_id: deviceId, // Explicitly include device_id for ID merge
  };
  // Track directly without sanitization to preserve $device_id
  mixpanel.track("identify_complete", identifyProps);
};

// Remove any device-related properties that Mixpanel might auto-add
const scrubDeviceData = (props) => {
  if (!props || typeof props !== "object") return props;
  const scrubbed = { ...props };
  
  // Remove only identifying device data (allow helpful non-identifying device info)
  const deviceFields = [
    "$device_id",
    "$browser_version", // Can be identifying when combined with other data
    "$screen_resolution", // Exact resolution can be identifying
    "$host",
    "device_id",
    "deviceId",
    "user_agent", // Full user agent can be identifying
    "userAgent",
    "browser_version",
    "screen_resolution",
    "screen_size", // Exact screen size can be identifying
    // Allow through: $os, $browser, platform, $screen_width, $screen_height, $referrer, $referring_domain, $current_url
    // These are helpful for analytics and not identifying on their own
  ];
  
  deviceFields.forEach((field) => {
    delete scrubbed[field];
  });
  
  return scrubbed;
};

export const trackMixpanelEvent = (eventName, props) => {
  if (!initialized) return;
  const sanitized = sanitizeMixpanelEvent(eventName, props);
  if (!sanitized) return;
  const scrubbed = scrubDeviceData(sanitized);
  mixpanel.track(eventName, scrubbed);
};
